import React from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { IconScout } from '@/components/ui/images/IconScout';
import { useMedicalLeaveDetailPanel } from '@/hooks/absence/useMedicalLeaveDetailPanel';
import { HALF_DAY_WORKDAY_TYPES } from '@/api/domains/absence/absence.types';
import { normalizeNumericSelectValue, normalizeSelectValue } from '@/utils/formHelpers';
import { colors } from '@/assets/styles/colors';


const HalfDayCountHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  return (
    <Text variant="small-regular" color={colors.important.main}>
      {translate('medicalLeave.form.halfDayCountHint')}
    </Text>
  );
};

export interface MedicalLeaveDetailPanelProps {
  medicalLeaveId: number;
}

export const MedicalLeaveDetailPanel: React.FC<MedicalLeaveDetailPanelProps> = ({
  medicalLeaveId,
}) => {
  const { t: translate } = useTranslation('absence');

  const {
    form,
    employeeName,
    isReadOnly,
    loadErrorMessage,
    isSubmitted,
    isSubmitting,
    handleSubmit,
    leaveTypeOptions,
    workdayTypeOptions,
  } = useMedicalLeaveDetailPanel({ medicalLeaveId });

  const fieldsDisabled = isReadOnly || isSubmitting;

  const startDateValue = form.watch('startDate');
  const workdayTypeValue = form.watch('workdayType');
  const dayCountValue = Number(form.watch('dayCount'));
  const showHalfDayCountHint =
    HALF_DAY_WORKDAY_TYPES.has(workdayTypeValue) && dayCountValue >= 1;

  if (loadErrorMessage) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 h-full"
        data-testid="medical-leave-detail-load-error"
      >
        <IconScout name="alert" color={colors.important.main} size="lg" />
        <Text variant="body-medium" color={colors.important.main}>
          {loadErrorMessage}
        </Text>
      </div>
    );
  }

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="medical-leave-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('medicalLeave.form.employee')}
          value={employeeName || '-'}
          disabled
          fullWidth
          data-testid="medical-leave-detail-employee"
        />

        <Selector
          label={translate('medicalLeave.form.leaveType')}
          placeholder={translate('medicalLeave.form.leaveTypePlaceholder')}
          options={leaveTypeOptions}
          value={form.watch('leaveTypeId') ? String(form.watch('leaveTypeId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeNumericSelectValue(value);
            if (numericValue !== undefined) {
              form.setValue('leaveTypeId', numericValue, { shouldDirty: true });
            }
          }}
          disabled={fieldsDisabled}
          color={form.errors.leaveTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.leaveTypeId && isSubmitted
              ? translate(form.errors.leaveTypeId.message as string)
              : undefined
          }
          required
          data-testid="medical-leave-detail-leave-type"
        />

        <LabelInput
          label={translate('medicalLeave.form.folio')}
          registration={form.register('folio')}
          disabled={fieldsDisabled}
          maxLength={50}
          color={form.errors.folio && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.folio && isSubmitted
              ? translate(form.errors.folio.message as string)
              : undefined
          }
          required
          data-testid="medical-leave-detail-folio"
        />

        <Selector
          label={translate('medicalLeave.form.workdayType')}
          placeholder={translate('medicalLeave.form.workdayTypePlaceholder')}
          options={workdayTypeOptions}
          value={form.watch('workdayType') || undefined}
          onChange={(value) => {
            const codeValue = normalizeSelectValue(value);
            if (codeValue) {
              form.setValue('workdayType', codeValue, { shouldDirty: true });
            }
          }}
          disabled={fieldsDisabled}
          color={form.errors.workdayType && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.workdayType && isSubmitted
              ? translate(form.errors.workdayType.message as string)
              : undefined
          }
          required
          data-testid="medical-leave-detail-workday-type"
        />

        <div className="flex flex-col gap-1">
          <LabelInput
            label={translate('medicalLeave.form.dayCount')}
            type="number"
            registration={form.register('dayCount')}
            disabled={fieldsDisabled}
            color={form.errors.dayCount && isSubmitted ? 'error' : undefined}
            supportingText={
              form.errors.dayCount && isSubmitted
                ? translate(form.errors.dayCount.message as string)
                : undefined
            }
            required
            data-testid="medical-leave-detail-day-count"
          />
          {showHalfDayCountHint && <HalfDayCountHint />}
        </div>

        <DatePicker
          label={translate('medicalLeave.form.startDate')}
          value={form.watch('startDate') || undefined}
          onChange={(date) => form.setValue('startDate', date, { shouldDirty: true })}
          disabled={fieldsDisabled}
          iconColor={colors.feedback.success300}
          color={form.errors.startDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.startDate && isSubmitted
              ? translate(form.errors.startDate.message as string)
              : undefined
          }
          required
          data-testid="medical-leave-detail-start-date"
        />

        <DatePicker
          label={translate('medicalLeave.form.endDate')}
          value={form.watch('endDate') || undefined}
          placeholder="-"
          disabled
          minDate={startDateValue || undefined}
          iconColor={colors.feedback.error300}
          data-testid="medical-leave-detail-end-date"
        />

        <DatePicker
          label={translate('medicalLeave.form.returnDate')}
          value={form.watch('returnDate') || undefined}
          placeholder="-"
          disabled
          data-testid="medical-leave-detail-return-date"
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="edit">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isReadOnly || isSubmitting}
            data-testid="submit-medical-leave-detail-button"
          >
            {translate('medicalLeave.form.submit')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default MedicalLeaveDetailPanel;
